import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { Avatar } from '../../internals/Avatar';
import { ICON_CLASS, TEXT_STYLE } from '../../tokens';
import { ICON_GLYPH } from '../Icon';

export type OptionValue = string | number;

type IconName = keyof typeof ICON_GLYPH;

export type Option = {
  value: OptionValue;
  label: string;
  avatar_url?: string | null;
  detail?: string;
  icon?: IconName;
  color?: string;
};

// A backend option with no avatar sends avatar_url as null, not an absent key
const hasAvatarUrl = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.length > 0;

// A menu renders in a portal outside the card's `ac-scale` frame, so it keeps the app's sizes
const MARK_SIZE = {
  card: {
    dot: 'size-[calc(8*var(--ac-u))]',
    icon: ICON_CLASS,
    gap: 'gap-[var(--ac-gap-sm)]',
    label: TEXT_STYLE.body,
    detail: TEXT_STYLE.meta,
  },
  menu: {
    dot: 'size-2',
    icon: 'size-3.5',
    gap: 'gap-2',
    label: 'text-sm leading-5',
    detail: 'text-xs leading-4',
  },
};

// The option's colour dot, icon and avatar, each only when it has one. An icon
// takes the option's colour in place of the dot, so it is the one mark, in the
// pill and in the menu alike
export const OptionMarks = ({
  option,
  avatarSize,
  inMenu = false,
}: {
  option: Option;
  avatarSize: number;
  inMenu?: boolean;
}) => {
  const Glyph = option.icon ? ICON_GLYPH[option.icon] : undefined;
  const tinted = Glyph !== undefined && Boolean(option.color);
  const size = MARK_SIZE[inMenu ? 'menu' : 'card'];
  return (
    <>
      {option.color && !tinted ? (
        <span
          aria-hidden="true"
          className={cn(size.dot, 'shrink-0 rounded-full')}
          style={{ backgroundColor: option.color }}
        />
      ) : null}
      {Glyph && tinted ? (
        <span className="flex shrink-0" style={{ color: option.color }}>
          <Glyph className={size.icon} aria-hidden="true" />
        </span>
      ) : Glyph ? (
        <Glyph
          className={cn(size.icon, 'shrink-0 text-foreground-muted')}
          aria-hidden="true"
        />
      ) : null}
      {hasAvatarUrl(option.avatar_url) ? (
        <Avatar
          id={String(option.value)}
          name={option.label}
          avatarUrl={option.avatar_url}
          shape="circle"
          size={avatarSize}
        />
      ) : null}
    </>
  );
};

// In a menu an option's label wraps to two lines before its ellipsis, so long
// options stay distinguishable; in a trigger it keeps one line
export const OptionContent = ({
  option,
  inMenu = false,
}: {
  option: Option;
  inMenu?: boolean;
}) => {
  const size = MARK_SIZE[inMenu ? 'menu' : 'card'];
  return (
    <XStack className={cn('min-w-0 items-center', size.gap)}>
      <OptionMarks option={option} avatarSize={20} inMenu={inMenu} />
      <YStack className="min-w-0">
        <span
          className={cn(
            size.label,
            'text-foreground',
            inMenu ? 'line-clamp-2 break-words' : 'truncate'
          )}
        >
          {option.label}
        </span>
        {option.detail ? (
          <span className={cn(size.detail, 'truncate text-foreground-muted')}>
            {option.detail}
          </span>
        ) : null}
      </YStack>
    </XStack>
  );
};
